import styled from 'styled-components';
import { ReactNode, createContext, useMemo } from 'react';

export type GridColumns = 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12;
export type GridSpacing = 'none' | 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl';
export type GridSpan = GridColumns | 'auto';

const spacingMap: Record<GridSpacing, number> = {
  none: 0,
  xs: 4,
  sm: 8,
  md: 16,
  lg: 24,
  xl: 32,
  '2xl': 48,
};

const resolveSpacing = (
  value: GridSpacing | number | undefined,
): number | undefined => {
  if (value === undefined) return undefined;
  if (typeof value === 'number') return value;
  return spacingMap[value];
};

// Context for passing gap to GridItem
const GridContext = createContext<{ gap: number }>({ gap: 16 });

const getGridColumn = (span: GridSpan, start?: number): string => {
  if (start) {
    const spanValue = span === 'auto' ? 1 : span;
    return `${start} / span ${spanValue}`;
  }
  return span === 'auto' ? 'auto' : `span ${span}`;
};

export interface GridProps {
  /** Children elements (should be GridItem) */
  children?: ReactNode;
  /** Number of columns (1-12) */
  columns?: GridColumns;
  /** Gap between items */
  gap?: GridSpacing | number;
  /** Row gap (overrides gap for rows) */
  rowGap?: GridSpacing | number;
  /** Column gap (overrides gap for columns) */
  columnGap?: GridSpacing | number;
  /** Alignment of items */
  alignItems?: 'start' | 'center' | 'end' | 'stretch';
  /** Additional class name */
  className?: string;
  /** Test id for testing */
  'data-testid'?: string;
}

export interface GridItemProps {
  /** Children elements */
  children?: ReactNode;
  /** Number of columns to span */
  span?: GridSpan;
  /** Column span at sm breakpoint (640px+) */
  smSpan?: GridSpan;
  /** Column span at md breakpoint (768px+) */
  mdSpan?: GridSpan;
  /** Column span at lg breakpoint (1024px+) */
  lgSpan?: GridSpan;
  /** Start column (1-based) */
  start?: number;
  /** Additional class name */
  className?: string;
  /** Test id for testing */
  'data-testid'?: string;
}

const StyledGrid = styled.div<{
  $columns: GridColumns;
  $gap: number;
  $rowGap?: number;
  $columnGap?: number;
  $alignItems: string;
}>`
  display: grid;
  grid-template-columns: repeat(${({ $columns }) => $columns}, minmax(0, 1fr));
  gap: ${({ $gap }) => $gap}px;
  ${({ $rowGap }) => ($rowGap === undefined ? '' : `row-gap: ${$rowGap}px;`)}
  ${({ $columnGap }) =>
    $columnGap === undefined ? '' : `column-gap: ${$columnGap}px;`}
  align-items: ${({ $alignItems }) => $alignItems};
  width: 100%;
`;

const StyledGridItem = styled.div<{
  $span: GridSpan;
  $smSpan?: GridSpan;
  $mdSpan?: GridSpan;
  $lgSpan?: GridSpan;
  $start?: number;
}>`
  grid-column: ${({ $span, $start }) => getGridColumn($span, $start)};

  @media (min-width: 640px) {
    ${({ $smSpan, $start }) =>
      $smSpan === undefined ? '' : `grid-column: ${getGridColumn($smSpan, $start)};`}
  }

  @media (min-width: 768px) {
    ${({ $mdSpan, $start }) =>
      $mdSpan === undefined ? '' : `grid-column: ${getGridColumn($mdSpan, $start)};`}
  }

  @media (min-width: 1024px) {
    ${({ $lgSpan, $start }) =>
      $lgSpan === undefined ? '' : `grid-column: ${getGridColumn($lgSpan, $start)};`}
  }
`;

/**
 * Grid component based on Libertify Design System
 *
 * A 12-column responsive grid system.
 *
 * @example
 * ```tsx
 * import { Grid, GridItem } from '@libertify/ui';
 *
 * // Basic 3-column grid
 * <Grid columns={12} gap="md">
 *   <GridItem span={4}>Column 1</GridItem>
 *   <GridItem span={4}>Column 2</GridItem>
 *   <GridItem span={4}>Column 3</GridItem>
 * </Grid>
 *
 * // Responsive grid
 * <Grid columns={12} gap="lg">
 *   <GridItem span={12} mdSpan={6} lgSpan={4}>
 *     Responsive column
 *   </GridItem>
 * </Grid>
 * ```
 */
export const Grid = ({
  children,
  columns = 12,
  gap = 'md',
  rowGap,
  columnGap,
  alignItems = 'stretch',
  className,
  'data-testid': testId,
}: GridProps) => {
  const gapValue = resolveSpacing(gap) ?? spacingMap.md;
  const rowGapValue = resolveSpacing(rowGap);
  const columnGapValue = resolveSpacing(columnGap);
  const contextValue = useMemo(() => ({ gap: gapValue }), [gapValue]);

  return (
    <GridContext.Provider value={contextValue}>
      <StyledGrid
        $columns={columns}
        $gap={gapValue}
        $rowGap={rowGapValue}
        $columnGap={columnGapValue}
        $alignItems={alignItems}
        className={className}
        data-testid={testId}
      >
        {children}
      </StyledGrid>
    </GridContext.Provider>
  );
};

/**
 * GridItem component - child of Grid
 *
 * @example
 * ```tsx
 * <GridItem span={6}>Half width</GridItem>
 * <GridItem span={12} mdSpan={6} lgSpan={3}>Responsive</GridItem>
 * ```
 */
export const GridItem = ({
  children,
  span = 1,
  smSpan,
  mdSpan,
  lgSpan,
  start,
  className,
  'data-testid': testId,
}: GridItemProps) => {
  return (
    <StyledGridItem
      $span={span}
      $smSpan={smSpan}
      $mdSpan={mdSpan}
      $lgSpan={lgSpan}
      $start={start}
      className={className}
      data-testid={testId}
    >
      {children}
    </StyledGridItem>
  );
};

export default Grid;
